import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { SectionCard } from "../AppShell";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { changeText, dayLabel, fmtDate } from "./adjustUtils";

const STATES = [["pending", "Awaiting approval"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]];
const SOURCE = { manual: "HR", regularization: "Regularization", overtime: "OT request" };

function RowActions({ a, mine, onDone }) {
  const act = async (path, body) => {
    try { await api.post(`/attendance-adjustments/${a.id}/${path}`, body); toast.success("Done"); onDone(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  if (a.state !== "pending") return null;
  if (mine) return <Button size="sm" variant="ghost" className="h-7" onClick={() => act("cancel")} data-testid={`adj-withdraw-${a.id}`}>Withdraw</Button>;
  return (
    <div className="flex gap-1 justify-end">
      <Button size="sm" className="h-7" onClick={() => act("approve", {})} data-testid={`adj-approve-${a.id}`}>Approve</Button>
      <Button size="sm" variant="outline" className="h-7" data-testid={`adj-reject-${a.id}`}
        onClick={() => { const c = window.prompt("Reason for rejecting (optional)"); if (c !== null) act("reject", { comment: c }); }}>Reject</Button>
    </div>
  );
}

const outcome = (a) => {
  if (a.state !== "approved") return a.decision_comment || "";
  const days = a.before_days !== a.after_days ? `${dayLabel(a.before_days)} → ${dayLabel(a.after_days)}` : "";
  const arr = a.settle === "arrears" ? ` · arrears ₹${Number(a.arrears_amount || 0).toLocaleString("en-IN")}` : "";
  return `${days}${arr}`;
};

export function AdjustmentRequests({ month, refreshKey, onChange }) {
  const { user } = useAuth();
  const [state, setState] = useState("pending");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState([]);
  const load = () => api.get(`/attendance-adjustments?month=${month}${state ? `&state=${state}` : ""}`).then(r => { setRows(r.data); setSel([]); });
  useEffect(() => { load(); }, [month, state, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = () => { load(); onChange(); };
  const approvable = rows.filter(a => a.state === "pending" && a.requested_by !== user?.id);
  const bulk = async () => {
    try {
      const r = await api.post("/attendance-adjustments/bulk-approve", { ids: sel });
      toast.success(`${r.data.approved} approved${r.data.skipped.length ? ` · ${r.data.skipped.length} skipped` : ""}`); done();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <SectionCard title="Adjustment log" subtitle="Every change with who asked, who approved, when and why" testid="adj-requests"
      action={<div className="flex flex-wrap gap-1">
        {STATES.map(([k, l]) => (
          <button key={k || "all"} onClick={() => setState(k)} data-testid={`adj-filter-${k || "all"}`}
            className={`px-3 h-8 rounded-full text-xs border transition-colors ${state === k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:bg-zinc-50"}`}>{l}</button>
        ))}
        {sel.length > 0 && <Button size="sm" className="h-8" onClick={bulk} data-testid="adj-bulk-approve">Approve {sel.length}</Button>}
      </div>}>
      {rows.length === 0 ? <div className="text-sm text-zinc-500 py-6 text-center" data-testid="adj-requests-empty">Nothing here for {month}.</div> : (
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-8">{approvable.length > 0 && <Checkbox checked={sel.length === approvable.length} onCheckedChange={v => setSel(v ? approvable.map(a => a.id) : [])} data-testid="adj-select-all"/>}</TableHead>
            <TableHead>Employee</TableHead><TableHead>Date</TableHead><TableHead>Change</TableHead><TableHead>Reason</TableHead>
            <TableHead>Asked by</TableHead><TableHead>Status</TableHead><TableHead/>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(a => {
              const mine = a.requested_by === user?.id;
              return (
                <TableRow key={a.id} data-testid={`adj-req-row-${a.id}`}>
                  <TableCell>{a.state === "pending" && !mine && <Checkbox data-testid={`adj-select-${a.id}`} checked={sel.includes(a.id)} onCheckedChange={v => setSel(v ? [...sel, a.id] : sel.filter(x => x !== a.id))}/>}</TableCell>
                  <TableCell><div className="font-medium">{a.employee_name}</div><div className="text-[10px] text-zinc-500">{a.employee_code}</div></TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(a.date)}</TableCell>
                  <TableCell><div>{changeText(a)}</div><div className="text-[10px] text-zinc-500">{outcome(a)}</div></TableCell>
                  <TableCell className="max-w-[220px] text-xs text-zinc-600">{a.reason}</TableCell>
                  <TableCell className="text-xs"><div>{a.requested_by_name}</div><div className="text-zinc-500">{SOURCE[a.source]} · {fmtDate(a.requested_at.slice(0, 10))}</div></TableCell>
                  <TableCell className="text-xs"><div className="capitalize">{a.state}</div>{a.decided_by_name && <div className="text-zinc-500">by {a.decided_by_name}</div>}</TableCell>
                  <TableCell className="text-right"><RowActions a={a} mine={mine} onDone={done}/></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
}
